import {AnalyticsSettingsButton} from './website-analytics';
import {useEffect, useRef, useState, type KeyboardEvent} from 'react';
import {PRODUCT_ENTRY_LABEL} from '../src/product';
import {Brand,ErrorNotice} from './components';
import type {ErrorDetails} from './lib/types';
import {ServiceBrand,serviceAtPath} from './service-brand';
import {APP_BASE, Link} from './router';
import {PUBLIC_MENUS, PUBLIC_NAVIGATION, PUBLIC_FOOTER_GROUPS, type PublicMenuKey, type PublicMenu} from './public-navigation';
import {PublicSectionRule} from './public-section-rule';
import './public-design.css';
import {PaymentAcceptance} from './payment-acceptance';
import {usePublicLocale} from './public-locale';

type NavigationSurface = {mode: 'desktop'; section: PublicMenuKey} | {mode: 'mobile'; section: PublicMenuKey | null} | null;

export function PublicHeader({path,signedIn,planEntry,workspaceHref,onSignOut,signOutBusy,signOutError}: {path:string;signedIn:boolean;planEntry:boolean;workspaceHref:string;onSignOut:()=>void;signOutBusy:boolean;signOutError:ErrorDetails|null}) {
  const {t} = usePublicLocale();
  const [surface, setSurface] = useState<NavigationSurface>(null);
  const root = useRef<HTMLElement>(null);
  const mobile = useRef<HTMLDivElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const triggers = useRef<Partial<Record<PublicMenuKey, HTMLButtonElement | null>>>({});
  const mobileTriggers = useRef<Partial<Record<PublicMenuKey, HTMLButtonElement | null>>>({});
  const mobileContent = useRef<HTMLDivElement>(null);
  const backButton = useRef<HTMLButtonElement>(null);
  const mobileOpen = surface?.mode === 'mobile';
  const section = surface?.section;

  useEffect(() => {setSurface(null);}, [path,signedIn,planEntry]);
  useEffect(() => {
    const outside = (event: PointerEvent) => {if (event.target instanceof Node && !root.current?.contains(event.target)) setSurface(null);};
    const breakpoint = window.matchMedia('(max-width: 1050px)');
    const resize = () => setSurface(null);
    document.addEventListener('pointerdown', outside);
    breakpoint.addEventListener('change', resize);
    return () => {document.removeEventListener('pointerdown', outside); breakpoint.removeEventListener('change', resize);};
  }, []);
  useEffect(() => {
    if (!mobileOpen) return;
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    mobile.current?.querySelector<HTMLButtonElement>('button')?.focus();
    return () => {document.body.style.overflow = before;};
  }, [mobileOpen]);
  useEffect(() => {
    if (!mobileOpen || !section) return;
    if (mobileContent.current) mobileContent.current.scrollTop = 0;
    backButton.current?.focus();
  }, [mobileOpen, section]);

  function close(restoreFocus = false) {
    if (restoreFocus) {
      if (mobileOpen) menuButton.current?.focus();
      else if (section) triggers.current[section]?.focus();
    }
    setSurface(null);
  }
  function keys(event: KeyboardEvent<HTMLElement>) {
    if (event.key === 'Escape') {event.preventDefault(); if (mobileOpen && section) back(); else close(true);}
    if (event.key === 'Tab' && mobileOpen && mobile.current) {
      const controls = [...mobile.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')].filter(element => element.getClientRects().length > 0);
      const first = controls[0]; const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {event.preventDefault(); last?.focus();}
      else if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first?.focus();}
    }
  }
  function back() {
    const previous = section;
    setSurface({mode: 'mobile', section: null});
    requestAnimationFrame(() => {if (previous) mobileTriggers.current[previous]?.focus();});
  }
  function openMobile() {
    const header = root.current!;
    const top = header.querySelector('.public-header-row')!.getBoundingClientRect().top;
    header.style.setProperty('--public-mobile-offset', `${Math.max(0, top)}px`);
    setSurface({mode: 'mobile', section: null});
  }
  function openWithKeyboard(event: KeyboardEvent<HTMLButtonElement>, key: PublicMenuKey) {
    if (event.key !== 'ArrowDown') return;
    event.preventDefault(); setSurface({mode: 'desktop', section: key});
    requestAnimationFrame(() => root.current?.querySelector<HTMLElement>('.public-mega-menu a')?.focus());
  }
  const activeMenu = section ? PUBLIC_MENUS[section] : null;
  const service=serviceAtPath(path);
  const accountEntry = signedIn ? {href: workspaceHref, label: PRODUCT_ENTRY_LABEL} : {href: `${APP_BASE}/register`, label: 'Sign up'};
  const accountAction = signedIn && planEntry
    ? <button type="button" className="public-pill" disabled={signOutBusy} onClick={onSignOut}>{t('Sign out')}</button>
    : <Link className="public-pill" href={accountEntry.href} onClick={() => close()}>{t(accountEntry.label)}</Link>;
  return <header ref={root} className={`site-header public-header${planEntry ? ' plan-entry-header' : ''}`} onKeyDown={keys}
    onPointerLeave={event => {if (event.pointerType === 'mouse' && surface?.mode === 'desktop') close();}}
    onBlur={event => {if (surface?.mode === 'desktop' && event.relatedTarget instanceof Node && !event.currentTarget.contains(event.relatedTarget)) close();}}>
    {!planEntry && <div className="public-announcement"><p>{t('Meet the team that turns your ideas into work.')}</p><Link href={`${APP_BASE}/how-it-works`}>{t('Discover EightBots')}</Link></div>}
    <div className="public-header-row">{service?<ServiceBrand product={service} linked/>:<Brand/>}
      <div className="public-navigation-shell"><nav className="public-desktop-navigation" aria-label={t('Main navigation')}>
        {PUBLIC_NAVIGATION.map(item => 'menu' in item ? <button key={item.menu} type="button"
          ref={element => {triggers.current[item.menu] = element;}}
          aria-expanded={surface?.mode === 'desktop' && section === item.menu} aria-controls="public-mega-menu"
          onClick={() => setSurface({mode: 'desktop', section: item.menu})} onKeyDown={event => openWithKeyboard(event, item.menu)}
          onPointerEnter={event => {if (event.pointerType === 'mouse') setSurface({mode: 'desktop', section: item.menu});}}>
          {t(PUBLIC_MENUS[item.menu].label)}</button>
          : <Link key={item.path} href={`${APP_BASE}${item.path}`} aria-current={path === item.path ? 'page' : undefined}
            onPointerEnter={() => close()}>{t(item.label)}</Link>)}
      </nav>
      {surface?.mode === 'desktop' && activeMenu && <div className={`public-mega-menu public-mega-menu--${section}`} id="public-mega-menu" aria-label={t(activeMenu.label)}>
        <div className="public-menu-intro"><p className="public-menu-label">{t('Explore EightBots')}</p>
          <Link href={`${APP_BASE}${activeMenu.overview.path}`} onClick={() => close()}>{t(activeMenu.overview.label)}</Link>
          <div className="public-menu-promo"><h2>{t(activeMenu.title)}</h2><p>{t(activeMenu.description)}</p></div></div>
        <MenuGroups menu={activeMenu} path={path} close={() => close()}/>
      </div>}</div>
      <div className="public-header-actions"><Link className="public-pill public-pill-secondary" href={`${APP_BASE}/contact-sales`}>{t('Contact sales')}</Link>
        {signedIn&&!planEntry?<details className="public-account-menu"><summary>{t('Account')}</summary><div className="public-account-menu-content">
          <Link href={accountEntry.href}>{t('Workspace')}</Link><Link href={`${APP_BASE}/messages`}>{t('Messages')}</Link><Link href={`${APP_BASE}/billing`}>{t('Usage & Billing')}</Link><Link href={`${APP_BASE}/settings`}>{t('Settings')}</Link>
          <button type="button" disabled={signOutBusy} onClick={onSignOut}>{t('Sign out')}</button>
          <ErrorNotice error={signOutError}/>
        </div></details>:accountAction}</div>
      <button ref={menuButton} type="button" className="public-menu-toggle" aria-label={t('Open navigation')} aria-expanded={mobileOpen}
        aria-controls="public-mobile-navigation" onClick={openMobile}><span/><span/></button>
    </div>
    {planEntry && <ErrorNotice error={signOutError}/>}
    {mobileOpen && <div ref={mobile} id="public-mobile-navigation" className="public-mobile-menu" role="dialog" aria-modal="true" aria-label={t('Navigation')}>
      <div className="public-mobile-top"><Brand/>{accountAction}<button type="button" className="public-menu-close" aria-label={t('Close navigation')} onClick={() => close(true)}><span aria-hidden="true">×</span></button></div>
      <div className="public-mobile-panel">
        {activeMenu && <div className="public-mobile-back"><button ref={backButton} type="button" onClick={back}>{t('Back')}</button><span>{t(activeMenu.label)}</span></div>}
        <div ref={mobileContent} className="public-mobile-content">
          {activeMenu ? <nav aria-label={t(activeMenu.label)}><div className="public-mobile-submenu"><p className="public-menu-label">{t('Explore EightBots')}</p><Link className="public-mobile-overview" href={`${APP_BASE}${activeMenu.overview.path}`} onClick={() => close()}>{t(activeMenu.overview.label)}</Link><MenuGroups menu={activeMenu} path={path} close={() => close()}/></div></nav>
          : <nav className="public-mobile-root" aria-label={t('Mobile navigation')}>{PUBLIC_NAVIGATION.map(item => 'menu' in item ? <button key={item.menu} ref={element => {mobileTriggers.current[item.menu] = element;}} type="button" aria-haspopup="true" onClick={() => setSurface({mode: 'mobile', section: item.menu})}>
            {t(PUBLIC_MENUS[item.menu].label)}<span aria-hidden="true">+</span></button>
          : <Link key={item.path} href={`${APP_BASE}${item.path}`} aria-current={path === item.path ? 'page' : undefined} onClick={() => close()}>{t(item.label)}</Link>)}</nav>}
        </div>
        {!activeMenu && <div className="public-mobile-actions"><Link className="public-pill public-pill-secondary" href={`${APP_BASE}/contact-sales`} onClick={() => close()}>{t('Contact sales')}</Link>
          {accountAction}
          {signedIn&&!planEntry&&<><Link href={`${APP_BASE}/messages`} onClick={()=>close()}>{t('Messages')}</Link><Link href={`${APP_BASE}/billing`} onClick={()=>close()}>{t('Usage & Billing')}</Link><Link href={`${APP_BASE}/settings`} onClick={()=>close()}>{t('Settings')}</Link><button type="button" className="text-button" disabled={signOutBusy} onClick={onSignOut}>{t('Sign out')}</button><ErrorNotice error={signOutError}/></>}
        </div>}
      </div>
    </div>}
  </header>;
}

function MenuGroups({menu, path, close}: {menu: PublicMenu; path: string; close: () => void}) {
  const {t} = usePublicLocale();
  return <div className="public-menu-groups">{menu.groups.map(group => <section key={group.label}><h3>{t(group.label)}</h3>
    <ul>{group.links.map(link => <li key={link.path}><Link href={`${APP_BASE}${link.path}`} aria-current={path === link.path ? 'page' : undefined} onClick={close}>{t(link.label)}</Link></li>)}</ul>
  </section>)}</div>;
}

export function PublicFooter() {
  const {t} = usePublicLocale();
  return <footer className="public-footer"><PublicSectionRule/><div className="public-footer-top"><Brand/></div>
    <nav className="public-footer-links" aria-label={t('Footer navigation')}>{PUBLIC_FOOTER_GROUPS.map(group => <section key={group.label}>
      <h2>{t(group.label)}</h2>
      <ul>{group.links.map(link => <li key={link.path}><Link href={`${APP_BASE}${link.path}`}>{t(link.label)}</Link></li>)}</ul>
    </section>)}</nav>
    <hr className="footer-divider"/>
    <PaymentAcceptance/>
    <hr className="footer-divider"/>
    <div className="public-footer-bottom"><span>EightBots</span><Link href={`${APP_BASE}/privacy`}>{t('Privacy policy')}</Link><Link href={`${APP_BASE}/terms`}>{t('Terms of service')}</Link><AnalyticsSettingsButton/></div>
  </footer>;
}
